import { useState, useEffect } from 'react';
import { MockUser } from '@/mocks/users';
import { API_BASE_URL, AUTH_TOKEN_KEY } from '@/services/api';

const AUTH_KEY = 'helios_auth_user';

export interface AuthState {
  user: MockUser | null;
  isAuthenticated: boolean;
}

export function getStoredUser(): MockUser | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MockUser;
  } catch {
    return null;
  }
}

/**
 * Чому вхід не вдався. Раніше всі випадки зливались у `null`, і екран показував
 * «Невірний логін або пароль» навіть тоді, коли API просто перезапускався або
 * браузер тримав зіпсований кеш preflight (саме так це проявилось у Chrome).
 */
export type LoginFailure =
  | { kind: 'credentials' }
  | { kind: 'rate_limited'; retryAfterSec: number }
  | { kind: 'unavailable' };

export async function loginEx(
  login: string, password: string,
): Promise<{ user: MockUser } | { error: LoginFailure }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login: login.trim(), password }),
    });
  } catch {
    // Мережа/CORS/сервер не відповідає — це НЕ проблема облікових даних.
    return { error: { kind: 'unavailable' } };
  }
  if (res.status === 429) {
    const d = await res.json().catch(() => ({} as any));
    return { error: { kind: 'rate_limited', retryAfterSec: Number(d?.retry_after_seconds) || 60 } };
  }
  if (res.status === 401) return { error: { kind: 'credentials' } };
  if (!res.ok) return { error: { kind: 'unavailable' } };
  const d = await res.json().catch(() => null as any);
  if (!d?.ok || !d.token || !d.user) return { error: { kind: 'unavailable' } };
  localStorage.setItem(AUTH_KEY, JSON.stringify(d.user));
  localStorage.setItem(AUTH_TOKEN_KEY, d.token);
  return { user: d.user as MockUser };
}

/** Сумісність зі старими викликами: null = будь-яка невдача. */
export async function login(login: string, password: string): Promise<MockUser | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login: login.trim(), password }),
    });
    if (!res.ok) return null;
    const d = await res.json();
    if (!d?.ok || !d.token || !d.user) return null;
    localStorage.setItem(AUTH_KEY, JSON.stringify(d.user));
    localStorage.setItem(AUTH_TOKEN_KEY, d.token);
    return d.user as MockUser;
  } catch {
    return null;
  }
}

export function logout(): void {
  localStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export function useAuth(): AuthState & { refresh: () => void } {
  const [user, setUser] = useState<MockUser | null>(getStoredUser);

  useEffect(() => {
    setUser(getStoredUser());
  }, []);

  const refresh = () => setUser(getStoredUser());

  return { user, isAuthenticated: user !== null, refresh };
}
